import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, Cloud, Sun, CloudRain, Wind, Droplets, 
  Thermometer, MapPin, RefreshCw, TrendingUp, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import TimelineButton from '../components/TimelineButton';
import { todayLocal } from '../utils/dateUtils';

const WeatherTracker = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingWeather, setFetchingWeather] = useState(false);
  const [currentWeather, setCurrentWeather] = useState(null);
  const [correlations, setCorrelations] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [cityInput, setCityInput] = useState('');
  const [locationPermission, setLocationPermission] = useState('prompt');

  useEffect(() => {
    fetchEntries();
    fetchUserLocation();
    fetchCorrelations();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/weather?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Error fetching weather entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserLocation = async () => {
    try {
      const response = await axios.get(`${API}/weather/user-location`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.location) {
        setUserLocation(response.data.location);
        setCityInput(response.data.location.city || '');
      }
    } catch (error) {
      console.error('Error fetching user location:', error);
    }
  };

  const fetchCorrelations = async () => {
    try {
      const response = await axios.get(`${API}/weather/correlations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCorrelations(response.data);
    } catch (error) {
      console.error('Error fetching correlations:', error);
    }
  };

  const requestGeolocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        setLocationPermission('granted');
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
        setUserLocation(location);
        await fetchWeatherByCoords(location.latitude, location.longitude);
        
        // Save location preference
        try {
          await axios.post(`${API}/weather/user-location`, location, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (e) {
          console.error('Error saving location:', e);
        }
      },
      (error) => {
        setLocationPermission('denied');
        toast.error('Location access denied. Please enter your city manually.');
      }
    );
  };

  const fetchWeatherByCoords = async (lat, lon) => {
    setFetchingWeather(true);
    try {
      const response = await axios.post(`${API}/weather/fetch`, 
        { latitude: lat, longitude: lon },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setCurrentWeather(response.data);
      
      // Auto-save today's weather
      const today = todayLocal();
      await axios.post(`${API}/weather`, {
        date: today,
        location: response.data.location,
        latitude: lat,
        longitude: lon,
        temperature: response.data.temperature,
        humidity: response.data.humidity,
        pressure: response.data.pressure,
        weather_condition: response.data.weather_condition
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      toast.success('Weather data saved!');
      fetchEntries();
    } catch (error) {
      console.error('Error fetching weather:', error);
      toast.error('Failed to fetch weather data');
    } finally {
      setFetchingWeather(false);
    }
  };

  const fetchWeatherByCity = async () => {
    const zipCode = cityInput.trim();
    
    // Validate zip code format
    if (!zipCode) {
      toast.error('Please enter your zip code');
      return;
    }
    
    if (!/^\d{5}$/.test(zipCode)) {
      toast.error('Please enter a valid 5-digit zip code');
      return;
    }
    
    setFetchingWeather(true);
    try {
      const response = await axios.post(`${API}/weather/fetch`, 
        { city: zipCode },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setCurrentWeather(response.data);
      
      // Save location preference
      try {
        await axios.post(`${API}/weather/user-location`, 
          { city: zipCode },
          { headers: { Authorization: `Bearer ${token}` }}
        );
      } catch (locErr) {
        console.error('Error saving location preference:', locErr);
      }
      
      // Auto-save today's weather
      const today = todayLocal();
      await axios.post(`${API}/weather`, {
        date: today,
        location: response.data.location,
        temperature: response.data.temperature,
        humidity: response.data.humidity,
        pressure: response.data.pressure,
        weather_condition: response.data.weather_condition
      }, { headers: { Authorization: `Bearer ${token}` }});
      
      toast.success('Weather data saved!');
      fetchEntries();
    } catch (error) {
      console.error('Error fetching weather:', error);
      toast.error('Failed to fetch weather data');
    } finally {
      setFetchingWeather(false);
    }
  };

  const getWeatherIcon = (condition) => {
    const lower = (condition || '').toLowerCase();
    if (lower.includes('rain') || lower.includes('drizzle')) return <CloudRain className="w-8 h-8 text-blue-500" />;
    if (lower.includes('cloud')) return <Cloud className="w-8 h-8 text-gray-500" />;
    if (lower.includes('clear') || lower.includes('sun')) return <Sun className="w-8 h-8 text-yellow-500" />;
    return <Cloud className="w-8 h-8 text-gray-400" />;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div className="space-y-8" data-testid="weather-tracker">
      <Button 
        onClick={() => navigate('/dashboard')} 
        variant="ghost" 
        className="text-primary hover:text-primary/80"
        data-testid="back-button"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Dashboard
      </Button>

      {/* Header */}
      <div className="bg-gradient-to-br from-sky-500/10 to-blue-500/10 rounded-xl p-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <Cloud className="w-10 h-10 text-sky-600" strokeWidth={1.5} />
              <h1 className="text-3xl font-nunito font-bold text-[#2D2D2D]">
                Weather Tracker
              </h1>
            </div>
            <p className="text-[#57534E]">
              Track weather patterns to identify environmental triggers for symptoms
            </p>
          </div>
          <TimelineButton />
        </div>
      </div>

      {/* Current Weather Section */}
      <Card className="p-6">
        <h2 className="text-xl font-bold text-[#2D2D2D] mb-4 flex items-center gap-2">
          <Thermometer className="w-5 h-5 text-sky-600" />
          Today's Weather
        </h2>
        
        {/* Location Input */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <Label className="text-[#57534E] mb-2 block">Enter your zip code</Label>
            <div className="flex gap-2">
              <Input
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                placeholder="e.g., 94102"
                className="flex-1"
                data-testid="city-input"
                maxLength={5}
                onKeyDown={(e) => e.key === 'Enter' && fetchWeatherByCity()}
              />
              <Button
                onClick={fetchWeatherByCity}
                disabled={fetchingWeather}
                className="bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="fetch-by-city-btn"
              >
                {fetchingWeather ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Get Weather'}
              </Button>
            </div>
          </div>
          
          <div className="flex items-end">
            <Button
              onClick={requestGeolocation}
              variant="outline"
              disabled={fetchingWeather}
              className="flex items-center gap-2"
              data-testid="use-location-btn"
            >
              <MapPin className="w-4 h-4" />
              Use My Location
            </Button>
          </div>
        </div>

        {/* Current Weather Display */}
        {currentWeather && (
          <div className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {getWeatherIcon(currentWeather.weather_condition)}
                <div>
                  <p className="text-4xl font-bold text-[#2D2D2D]">{currentWeather.temperature}°F</p>
                  <p className="text-[#57534E]">{currentWeather.weather_condition}</p>
                  <p className="text-sm text-[#A8A29E]">{currentWeather.location}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Droplets className="w-4 h-4 text-blue-500" />
                  <span>{currentWeather.humidity}% Humidity</span>
                </div>
                <div className="flex items-center gap-2">
                  <Wind className="w-4 h-4 text-gray-500" />
                  <span>{currentWeather.wind_speed} mph</span>
                </div>
                <div className="flex items-center gap-2 col-span-2">
                  <TrendingUp className="w-4 h-4 text-purple-500" />
                  <span>{currentWeather.pressure} mb Pressure</span>
                </div>
              </div>
            </div>
            
            {currentWeather.is_mock && (
              <div className="mt-4 flex items-center gap-2 text-amber-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>Using sample data. Configure OpenWeather API key for real data.</span>
              </div>
            )}
          </div>
        )}

        {!currentWeather && (
          <div className="text-center py-8 text-[#57534E]">
            <Cloud className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <p>Enter your city or use your location to fetch today's weather</p>
          </div>
        )}
      </Card>

      {/* Weather Correlations */}
      {correlations && correlations.has_data && (
        <Card className="p-6">
          <h2 className="text-xl font-bold text-[#2D2D2D] mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            Weather & Symptom Correlations
          </h2>
          
          <div className="space-y-4">
            {correlations.correlations.map((corr, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-stone-50 rounded-lg">
                <div>
                  <p className="font-semibold text-[#2D2D2D]">{corr.factor}</p>
                  <p className="text-sm text-[#57534E]">{corr.insight}</p>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-bold ${corr.flare_rate > 50 ? 'text-red-500' : corr.flare_rate > 25 ? 'text-amber-500' : 'text-green-500'}`}>
                    {corr.flare_rate}%
                  </p>
                  <p className="text-xs text-[#A8A29E]">{corr.sample_size} days</p>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-sm text-[#A8A29E] mt-4">
            Based on {correlations.total_weather_days} weather days and {correlations.total_flare_days} flare days
          </p>
        </Card>
      )}

      {/* Recent Weather History */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-[#2D2D2D]">Weather History</h2>
        
        {loading ? (
          <p className="text-center text-[#57534E] py-8">Loading...</p>
        ) : entries.length === 0 ? (
          <Card className="p-8 text-center">
            <Cloud className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <p className="text-[#57534E]">No weather data yet. Fetch today's weather to start tracking!</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {entries.slice(0, 14).map((entry) => (
              <Card key={entry.id} className="p-4" data-testid={`weather-entry-${entry.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-[#2D2D2D]">{formatDate(entry.date)}</p>
                    <p className="text-sm text-[#57534E]">{entry.location}</p>
                  </div>
                  {getWeatherIcon(entry.weather_condition)}
                </div>
                
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-1">
                    <Thermometer className="w-3 h-3 text-red-400" />
                    <span>{entry.temperature}°F</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Droplets className="w-3 h-3 text-blue-400" />
                    <span>{entry.humidity}%</span>
                  </div>
                  {entry.pressure && (
                    <div className="flex items-center gap-1 col-span-2">
                      <TrendingUp className="w-3 h-3 text-purple-400" />
                      <span>{entry.pressure} mb</span>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WeatherTracker;
